import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import { Link } from '@/components/links';
import { StackCards } from '@/components/StackCards';
import { Card } from '@/components/StackCards/types';
import IconExtend from '@/assets/icons/heroIcons/Extend.svg';
import IconBilling from '@/assets/icons/heroIcons/Billing.svg';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconPay from '@/assets/icons/heroIcons/Pay.svg';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import Hero from '@/sections/Hero';
import MoreInfoSection from '@/sections/MoreInfo';
import { Calculator } from '@/sections/postponeDueDate/Calculator';
import { FaqIntro } from '@/sections/FaqIntro';
import { Button } from '@/components/buttons';
import { InfoBoxes } from '@/components/boxes';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { SectionHeadline } from '@/components/SectionHeadline';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import React, { useRef } from 'react';
import { mergeClasses } from '@/utils/style';
import { NumericRange } from '@/types/range';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { SeoHeader } from '@/components/SeoHeader';
import { Text } from '@/components/typography';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { TryItForFree } from '@/sections/postponeDueDate/TryItForFree';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(160);
  usePageView("postpone-due-date");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["postponeDueDate", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    pageBanner,
    menuPromoBanners
  } = props;
  const calculateRef = useRef(null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (calculateRef.current) {
        calculateRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleScrollToCalculate = t1;
  let t2;
  if ($[2] !== t) {
    t2 = Array.from({
      length: 5
    }, (_, index) => {
      const cardIndex = index + 1 as NumericRange<1, 5>;
      return {
        index,
        menuTitle: "",
        title: <div className={mergeClasses(cardIndex === 5 && "text-center lg:text-left")}>{t(`howItWorks.items.${cardIndex}.title`)}</div>,
        ...(cardIndex < 5 ? {
          description: <Trans t={t} i18nKey={`howItWorks.items.${cardIndex}.text`}><Link to={LINKS().registration} testId="postponeDueDateRegistration" analyticsParam="postponeDueDateRegistration" /></Trans>
        } : {
          footer: <Button analyticsParam="postponeDueDateHowItWorksiWantMaxi" testId="postponeDueDateHowItWorksiWantMaxi" variant="primary" size="lg" className="m-auto w-full md:w-auto lg:m-0" to={LINKS().registration}>{t(`howItWorks.items.${cardIndex}.text`)}</Button>
        }),
        images: {
          desktop: {
            src: `/images/sections/postponeDueDate/cards/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          mobile: {
            src: `/images/sections/postponeDueDate/cards/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          ...(cardIndex < 5 && {
            variant: "appMockup"
          })
        }
      };
    });
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const cards = t2;
  let t3;
  if ($[4] !== t) {
    t3 = t("seo.title");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("seo.description");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <SeoHeader title={t3} description={t4} />;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = pageBanner?.videoId;
  let t7;
  if ($[11] !== pageBanner?.title || $[12] !== t) {
    t7 = pageBanner?.title ?? t("banner.title");
    $[11] = pageBanner?.title;
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== pageBanner?.text || $[15] !== t) {
    t8 = pageBanner?.text ?? t("banner.text");
    $[14] = pageBanner?.text;
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== pageBanner || $[18] !== t) {
    t9 = pageBanner ? pageBanner.buttons : [{
      ariaLabel: t("banner.cta"),
      label: t("banner.cta"),
      to: LINKS().registration,
      testId: "postponeDueDateHeroPrimaryCTA",
      analyticsParam: "postponeDueDateHeroPrimaryCTA",
      variant: "primary"
    }, {
      ariaLabel: t("banner.calculateCTA"),
      label: t("banner.calculateCTA"),
      onClick: handleScrollToCalculate,
      testId: "calculateCTA",
      analyticsParam: "calculateCTA",
      variant: "secondary"
    }];
    $[17] = pageBanner;
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = pageBanner?.image?.url ?? "/images/sections/postponeDueDate/hero/cs/hero.png";
  let t11;
  if ($[20] !== pageBanner?.image?.alt || $[21] !== t) {
    t11 = pageBanner?.image?.alt ?? t("banner.title");
    $[20] = pageBanner?.image?.alt;
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = {
      url: t10,
      alt: t11
    };
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = pageBanner?.variant || "mockupMobile";
  let t14;
  if ($[26] !== t12 || $[27] !== t13 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t14 = <Hero videoId={t6} title={t7} text={t8} buttons={t9} image={t12} variant={t13} />;
    $[26] = t12;
    $[27] = t13;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="bg-blue-50"><SectionDelimiter color="bg-blue-100" background="bg-transparent" /></div>;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t) {
    t16 = t("advantages.title");
    $[34] = t;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t16) {
    t17 = <SectionHeadline className="mx-auto mb-10 max-w-192 px-0 md:mb-16" anchorKey="advantages.title">{t16}</SectionHeadline>;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t) {
    t18 = t("advantages.items.1.title");
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t) {
    t19 = t("advantages.items.1.text");
    $[40] = t;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <IconBilling />;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t18 || $[44] !== t19) {
    t21 = {
      title: t18,
      paragraph: t19,
      icon: t20
    };
    $[43] = t18;
    $[44] = t19;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t) {
    t22 = t("advantages.items.2.title");
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t) {
    t23 = t("advantages.items.2.text");
    $[48] = t;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <IconPay />;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== t22 || $[52] !== t23) {
    t25 = {
      title: t22,
      paragraph: t23,
      icon: t24
    };
    $[51] = t22;
    $[52] = t23;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t) {
    t26 = t("advantages.items.3.title");
    $[54] = t;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t) {
    t27 = t("advantages.items.3.text");
    $[56] = t;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <IconExtend />;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== t26 || $[60] !== t27) {
    t29 = {
      title: t26,
      paragraph: t27,
      icon: t28
    };
    $[59] = t26;
    $[60] = t27;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== t21 || $[63] !== t25 || $[64] !== t29) {
    t30 = <InfoBoxes items={[t21, t25, t29]} variant="basic" />;
    $[62] = t21;
    $[63] = t25;
    $[64] = t29;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t) {
    t31 = t("advantages.text");
    $[66] = t;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== t31) {
    t32 = <Text variant="p4" className="mx-auto max-w-142 text-center text-gray-800">{t31}</Text>;
    $[68] = t31;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = LINKS();
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  const t34 = t33.registration;
  let t35;
  if ($[71] !== t) {
    t35 = t("advantages.cta");
    $[71] = t;
    $[72] = t35;
  } else {
    t35 = $[72];
  }
  let t36;
  if ($[73] !== t35) {
    t36 = <Button to={t34} testId="postponeDueDateRegistration" analyticsParam="postponeDueDateRegistration" size="lg" variant="primary" align="center">{t35}</Button>;
    $[73] = t35;
    $[74] = t36;
  } else {
    t36 = $[74];
  }
  let t37;
  if ($[75] !== t30 || $[76] !== t32 || $[77] !== t36) {
    t37 = <div className="flex flex-col gap-10 lg:gap-12">{t30}{t32}{t36}</div>;
    $[75] = t30;
    $[76] = t32;
    $[77] = t36;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] !== t17 || $[80] !== t37) {
    t38 = <div className="relative z-10">{t17}{t37}</div>;
    $[79] = t17;
    $[80] = t37;
    $[81] = t38;
  } else {
    t38 = $[81];
  }
  let t39;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <div className="bgCircle absolute top-12 right-0 h-45 w-[11.25rem] translate-x-1/2 lg:top-0 lg:left-0 lg:h-125 lg:w-125 lg:-translate-x-1/4" />;
    $[82] = t39;
  } else {
    t39 = $[82];
  }
  let t40;
  if ($[83] !== t38) {
    t40 = <div className="relative z-30 overflow-hidden bg-blue-100 px-4 pt-10 pb-20 lg:pt-20 lg:pb-40">{t38}{t39}</div>;
    $[83] = t38;
    $[84] = t40;
  } else {
    t40 = $[84];
  }
  let t41;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="bg-blue-100"><SectionDelimiter color="bg-blue-50" background="bg-transparent" /></div>;
    $[85] = t41;
  } else {
    t41 = $[85];
  }
  let t42;
  let t43;
  let t44;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <div className="relative bg-blue-50 px-4 pt-10 pb-20 lg:pt-20 lg:pb-40" id="posunuti-zdarma"><TryItForFree /></div>;
    t43 = <SectionDelimiter color="bg-blue-700" background="bg-blue-50" wrapperClassName="hidden md:block" />;
    t44 = <SectionDelimiter color="bg-blue-900" background="bg-blue-50" wrapperClassName="md:hidden" />;
    $[86] = t42;
    $[87] = t43;
    $[88] = t44;
  } else {
    t42 = $[86];
    t43 = $[87];
    t44 = $[88];
  }
  let t45;
  if ($[89] !== t) {
    t45 = t("calculator.title");
    $[89] = t;
    $[90] = t45;
  } else {
    t45 = $[90];
  }
  let t46;
  if ($[91] !== t45) {
    t46 = <SectionHeadline className="mb-10 px-4 md:mb-16" variant="inverted" anchorKey="postponeDueDate.calculator.title">{t45}</SectionHeadline>;
    $[91] = t45;
    $[92] = t46;
  } else {
    t46 = $[92];
  }
  let t47;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <Calculator />;
    $[93] = t47;
  } else {
    t47 = $[93];
  }
  let t48;
  if ($[94] !== t46) {
    t48 = <div className="relative z-10">{t46}{t47}</div>;
    $[94] = t46;
    $[95] = t48;
  } else {
    t48 = $[95];
  }
  let t49;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <div className="bgCircle absolute top-[10%] left-2/3" />;
    $[96] = t49;
  } else {
    t49 = $[96];
  }
  let t50;
  if ($[97] !== t48) {
    t50 = <div className="relative overflow-x-hidden bg-blue-900 pt-10 pb-20 md:bg-blue-700 lg:pt-20 lg:pb-40" ref={calculateRef} id="calculate-anchor">{t48}{t49}</div>;
    $[97] = t48;
    $[98] = t50;
  } else {
    t50 = $[98];
  }
  let t51;
  let t52;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <SectionDelimiter color="bg-blue-100" background="bg-blue-700" wrapperClassName="hidden md:block" />;
    t52 = <SectionDelimiter color="bg-blue-100" background="bg-blue-900" wrapperClassName="md:hidden" />;
    $[99] = t51;
    $[100] = t52;
  } else {
    t51 = $[99];
    t52 = $[100];
  }
  let t53;
  if ($[101] !== t) {
    t53 = t("howItWorks.title");
    $[101] = t;
    $[102] = t53;
  } else {
    t53 = $[102];
  }
  let t54;
  if ($[103] !== t53) {
    t54 = <div className="mx-auto max-w-192"><SectionHeadline className="mb-10 md:mb-16" anchorKey="howItWorks.title">{t53}</SectionHeadline></div>;
    $[103] = t53;
    $[104] = t54;
  } else {
    t54 = $[104];
  }
  let t55;
  if ($[105] !== cards) {
    t55 = <StackCards imagePosition="left" cards={cards} />;
    $[105] = cards;
    $[106] = t55;
  } else {
    t55 = $[106];
  }
  let t56;
  if ($[107] !== t54 || $[108] !== t55) {
    t56 = <div className="relative z-10">{t54}{t55}</div>;
    $[107] = t54;
    $[108] = t55;
    $[109] = t56;
  } else {
    t56 = $[109];
  }
  let t57;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <div className="absolute top-3/4 right-0 overflow-hidden"><div className="bgCircle absolute top-0 right-0 translate-x-1/3 rotate-90" /></div>;
    $[110] = t57;
  } else {
    t57 = $[110];
  }
  let t58;
  if ($[111] !== t56) {
    t58 = <div className="relative bg-blue-100 pt-10 pb-20 lg:pt-20 lg:pb-40">{t56}{t57}</div>;
    $[111] = t56;
    $[112] = t58;
  } else {
    t58 = $[112];
  }
  let t59;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = LINKS();
    $[113] = t59;
  } else {
    t59 = $[113];
  }
  let t60;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <Link to="/caste-dotazy/dalsi-caste-otazky" analyticsParam="faqPostponeDueDate" testId="faqPostponeDueDate" />;
    $[114] = t60;
  } else {
    t60 = $[114];
  }
  let t61;
  if ($[115] !== t) {
    t61 = <div className="py-10 lg:py-20"><FaqIntro link={t59.faq} text={<Trans i18nKey="faq.text" t={t}>{t60}</Trans>} /></div>;
    $[115] = t;
    $[116] = t61;
  } else {
    t61 = $[116];
  }
  let t62;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = <IconDeferredPayment className="fillButtonIcon" />;
    $[117] = t62;
  } else {
    t62 = $[117];
  }
  let t63;
  if ($[118] !== t) {
    t63 = t("core:link.deferredPayment");
    $[118] = t;
    $[119] = t63;
  } else {
    t63 = $[119];
  }
  let t64;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = LINKS();
    $[120] = t64;
  } else {
    t64 = $[120];
  }
  let t65;
  if ($[121] !== t63) {
    t65 = {
      analyticsParam: "postponeDueDateMoreInfoDeferredPayment",
      icon: t62,
      testId: "postponeDueDateMoreInfoDeferredPayment",
      title: t63,
      to: t64.deferredPayment
    };
    $[121] = t63;
    $[122] = t65;
  } else {
    t65 = $[122];
  }
  let t66;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <IconMaxi className="fillButtonIcon" />;
    $[123] = t66;
  } else {
    t66 = $[123];
  }
  let t67;
  if ($[124] !== t) {
    t67 = t("core:products.revolving");
    $[124] = t;
    $[125] = t67;
  } else {
    t67 = $[125];
  }
  let t68;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = LINKS();
    $[126] = t68;
  } else {
    t68 = $[126];
  }
  let t69;
  if ($[127] !== t67) {
    t69 = {
      analyticsParam: "postponeDueDateMoreInfoRevolvingApplication",
      icon: t66,
      testId: "postponeDueDateMoreInfoRevolvingApplication",
      title: t67,
      to: t68.revolving
    };
    $[127] = t67;
    $[128] = t69;
  } else {
    t69 = $[128];
  }
  let t70;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = <IconThird className="fillButtonIcon" />;
    $[129] = t70;
  } else {
    t70 = $[129];
  }
  let t71;
  if ($[130] !== t) {
    t71 = t("core:products.payInThree");
    $[130] = t;
    $[131] = t71;
  } else {
    t71 = $[131];
  }
  let t72;
  if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = LINKS();
    $[132] = t72;
  } else {
    t72 = $[132];
  }
  let t73;
  if ($[133] !== t71) {
    t73 = {
      analyticsParam: "postponeDueDateMoreInfoPayInThree",
      icon: t70,
      testId: "postponeDueDateMoreInfoPayInThree",
      title: t71,
      to: t72.payInThree
    };
    $[133] = t71;
    $[134] = t73;
  } else {
    t73 = $[134];
  }
  let t74;
  if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <IconCard className="fillButtonIcon" />;
    $[135] = t74;
  } else {
    t74 = $[135];
  }
  let t75;
  if ($[136] !== t) {
    t75 = t("core:products.card");
    $[136] = t;
    $[137] = t75;
  } else {
    t75 = $[137];
  }
  let t76;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t76 = LINKS();
    $[138] = t76;
  } else {
    t76 = $[138];
  }
  let t77;
  if ($[139] !== t75) {
    t77 = {
      analyticsParam: "postponeDueDateMoreInfoCard",
      icon: t74,
      testId: "postponeDueDateMoreInfoCard",
      title: t75,
      to: t76.card
    };
    $[139] = t75;
    $[140] = t77;
  } else {
    t77 = $[140];
  }
  let t78;
  if ($[141] !== t65 || $[142] !== t69 || $[143] !== t73 || $[144] !== t77) {
    t78 = <MoreInfoSection buttons={[t65, t69, t73, t77]} />;
    $[141] = t65;
    $[142] = t69;
    $[143] = t73;
    $[144] = t77;
    $[145] = t78;
  } else {
    t78 = $[145];
  }
  let t79;
  if ($[146] !== t61 || $[147] !== t78) {
    t79 = <div className="relative z-10">{t61}{t78}</div>;
    $[146] = t61;
    $[147] = t78;
    $[148] = t79;
  } else {
    t79 = $[148];
  }
  let t80;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t80 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[149] = t80;
  } else {
    t80 = $[149];
  }
  let t81;
  if ($[150] !== t79) {
    t81 = <div className="relative overflow-hidden bg-blue-100">{t79}{t80}</div>;
    $[150] = t79;
    $[151] = t81;
  } else {
    t81 = $[151];
  }
  let t82;
  if ($[152] !== menuPromoBanners || $[153] !== t14 || $[154] !== t40 || $[155] !== t5 || $[156] !== t50 || $[157] !== t58 || $[158] !== t81) {
    t82 = <Layout menuPromoBanners={menuPromoBanners}>{t5}{t14}{t15}{t40}{t41}{t42}{t43}{t44}{t50}{t51}{t52}{t58}{t81}</Layout>;
    $[152] = menuPromoBanners;
    $[153] = t14;
    $[154] = t40;
    $[155] = t5;
    $[156] = t50;
    $[157] = t58;
    $[158] = t81;
    $[159] = t82;
  } else {
    t82 = $[159];
  }
  return t82;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('odlozeni-splatnosti');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      pageBanner: await getPageBannerBySlug('postpone-due-date'),
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      ...(await getIntlProps(ctx, ['core', 'postponeDueDate']))
    }
  };
}) satisfies GetStaticProps<Props>;